import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Eraser, PenLine } from "lucide-react";
import { BrandLockup } from "@/components/landing/LogoMark";
import { platformClient } from "./platformApi";

const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";
const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";

export default function SignPage() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const party = searchParams.get("party") === "clarke" ? "clarke" : "klant";
    const [doc, setDoc] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [name, setName] = useState("");
    const [func, setFunc] = useState("");
    const [hasInk, setHasInk] = useState(false);
    const canvasRef = useRef(null);
    const drawing = useRef(false);

    const load = async () => {
        try {
            const { data } = await platformClient.get(`/public/sign/${token}`);
            setDoc(data);
            if (party === "klant") {
                setName(data.client_contact || "");
                setFunc(data.client_contact_function || "");
            } else {
                setName("Raymond Clarke");
                setFunc("Directeur");
            }
        } catch {
            setError("Deze tekenlink is ongeldig of verlopen.");
        }
    };

    useEffect(() => {
        load();
    }, [token]);

    // Canvas tekenlogica (muis + aanraking)
    useEffect(() => {
        const cv = canvasRef.current;
        if (!cv) return;
        const ctx = cv.getContext("2d");
        const ratio = window.devicePixelRatio || 1;
        const rect = cv.getBoundingClientRect();
        cv.width = rect.width * ratio;
        cv.height = rect.height * ratio;
        ctx.scale(ratio, ratio);
        ctx.strokeStyle = "#1a1a1a";
        ctx.lineWidth = 2.2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        const pos = (e) => {
            const r = cv.getBoundingClientRect();
            const p = e.touches ? e.touches[0] : e;
            return { x: p.clientX - r.left, y: p.clientY - r.top };
        };
        const start = (e) => {
            drawing.current = true;
            const { x, y } = pos(e);
            ctx.beginPath();
            ctx.moveTo(x, y);
            e.preventDefault();
        };
        const move = (e) => {
            if (!drawing.current) return;
            const { x, y } = pos(e);
            ctx.lineTo(x, y);
            ctx.stroke();
            setHasInk(true);
            e.preventDefault();
        };
        const end = () => {
            drawing.current = false;
        };
        cv.addEventListener("mousedown", start);
        cv.addEventListener("mousemove", move);
        window.addEventListener("mouseup", end);
        cv.addEventListener("touchstart", start, { passive: false });
        cv.addEventListener("touchmove", move, { passive: false });
        cv.addEventListener("touchend", end);
        return () => {
            cv.removeEventListener("mousedown", start);
            cv.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", end);
            cv.removeEventListener("touchstart", start);
            cv.removeEventListener("touchmove", move);
            cv.removeEventListener("touchend", end);
        };
    }, [doc]);

    const clearCanvas = () => {
        const cv = canvasRef.current;
        if (!cv) return;
        cv.getContext("2d").clearRect(0, 0, cv.width, cv.height);
        setHasInk(false);
    };

    const submit = async () => {
        if (!name.trim() || !hasInk) return;
        setBusy(true);
        setError("");
        try {
            const png = canvasRef.current.toDataURL("image/png");
            await platformClient.post(`/public/sign/${token}`, { party, name: name.trim(), function: func.trim(), png });
            setDone(true);
            await load();
        } catch (e) {
            setError(e.response?.data?.detail || "Tekenen mislukt — probeer het opnieuw.");
        } finally {
            setBusy(false);
        }
    };

    const alreadySigned = doc && (party === "klant" ? doc.signed_klant : doc.signed_clarke);

    return (
        <div className="min-h-screen bg-ink px-4 py-10 text-bone">
            <div className="mx-auto max-w-3xl">
                <div className="flex justify-center"><BrandLockup /></div>
                {error && !doc ? (
                    <p data-testid="sign-error" className="mt-10 text-center text-sm text-red-400">{error}</p>
                ) : !doc ? (
                    <p className="mt-10 text-center text-sm text-ash">Document laden…</p>
                ) : (
                    <div className="mt-8 space-y-6">
                        <div className="text-center">
                            <p className={LABEL}>{doc.doc_label} — digitaal ondertekenen</p>
                            <h1 className="mt-2 font-display text-3xl">{doc.number}</h1>
                            <p className="mt-1 text-sm text-ash">{doc.title} · {doc.client_name} · {doc.issue_date}</p>
                        </div>

                        {/* Het document zoals beide partijen het krijgen */}
                        <div className="space-y-3 border border-white/10 bg-coal p-3" data-testid="sign-document-pages">
                            {doc.pages.map((pg, i) => (
                                <img key={i} src={`data:image/png;base64,${pg}`} alt={`Pagina ${i + 1}`} className="w-full border border-white/10" />
                            ))}
                        </div>

                        {/* Status van beide partijen */}
                        <div className="grid grid-cols-2 gap-3 text-center">
                            <div data-testid="sign-status-klant" className={`border p-3 text-xs ${doc.signed_klant ? "border-green-500/40 text-green-400" : "border-white/10 text-ash"}`}>
                                {doc.signed_klant ? <CheckCircle2 className="mx-auto mb-1 h-4 w-4" /> : <PenLine className="mx-auto mb-1 h-4 w-4" />}
                                Opdrachtgever {doc.signed_klant ? `heeft getekend (${doc.signatures?.klant?.date})` : "moet nog tekenen"}
                            </div>
                            <div data-testid="sign-status-clarke" className={`border p-3 text-xs ${doc.signed_clarke ? "border-green-500/40 text-green-400" : "border-white/10 text-ash"}`}>
                                {doc.signed_clarke ? <CheckCircle2 className="mx-auto mb-1 h-4 w-4" /> : <PenLine className="mx-auto mb-1 h-4 w-4" />}
                                Clarke Business Solutions {doc.signed_clarke ? `heeft getekend (${doc.signatures?.clarke?.date})` : "moet nog tekenen"}
                            </div>
                        </div>

                        {done || alreadySigned ? (
                            <div data-testid="sign-done" className="border border-green-500/30 bg-green-500/5 p-6 text-center">
                                <CheckCircle2 className="mx-auto h-8 w-8 text-green-400" />
                                <p className="mt-3 text-sm">Je handtekening is geplaatst en opgeslagen bij dit document.</p>
                                {doc.signed_klant && doc.signed_clarke && (
                                    <p className="mt-1 text-xs text-green-400">Beide partijen hebben getekend — het document is volledig ondertekend.</p>
                                )}
                            </div>
                        ) : (
                            <div className="border border-white/10 bg-coal p-6" data-testid="sign-form">
                                <p className={LABEL}>Tekenen als {party === "klant" ? "opdrachtgever" : "Clarke Business Solutions"}</p>
                                <div className="mt-4 grid grid-cols-2 gap-4">
                                    <label className="block">
                                        <span className={LABEL}>Naam</span>
                                        <input data-testid="sign-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
                                    </label>
                                    <label className="block">
                                        <span className={LABEL}>Functie</span>
                                        <input data-testid="sign-function" value={func} onChange={(e) => setFunc(e.target.value)} className={inputCls} />
                                    </label>
                                </div>
                                <div className="mt-5">
                                    <div className="flex items-center justify-between">
                                        <span className={LABEL}>Jouw handtekening</span>
                                        <button type="button" data-testid="sign-clear" onClick={clearCanvas} className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-ash hover:text-gold">
                                            <Eraser className="h-3 w-3" /> Wissen
                                        </button>
                                    </div>
                                    <canvas
                                        ref={canvasRef}
                                        data-testid="sign-canvas"
                                        className="mt-2 h-40 w-full cursor-crosshair touch-none border border-white/20 bg-white"
                                    />
                                    <p className="mt-1 text-[10px] text-ash/60">Teken met je muis of vinger. Door te versturen ga je akkoord met het document hierboven.</p>
                                </div>
                                {error ? <p data-testid="sign-submit-error" className="mt-3 text-sm text-red-400">{error}</p> : null}
                                <button
                                    data-testid="sign-submit"
                                    onClick={submit}
                                    disabled={busy || !name.trim() || !hasInk}
                                    className="mt-5 w-full border border-gold/50 py-3 text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black disabled:opacity-40"
                                >
                                    {busy ? "Bezig…" : "Handtekening plaatsen"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
